
import React from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Sidebar } from './Navigation';
import { LojistaNavigation } from './shopkeeper/LojistaNavigation';
import { useAuth } from './AuthProvider';
import { motion, AnimatePresence } from 'motion/react';

interface LayoutProps {
  cartCount: number;
  unreadNotifs: number;
  isSidebarCollapsed: boolean;
  setIsSidebarCollapsed: (value: boolean) => void;
  isMobileMenuOpen: boolean;
  setIsMobileMenuOpen: (value: boolean) => void;
  activeGame: number | 'All';
  setActiveGame: (value: number | 'All') => void;
  dbGames: any[];
}

export const Layout: React.FC<LayoutProps> = ({
  cartCount,
  unreadNotifs,
  isSidebarCollapsed,
  setIsSidebarCollapsed,
  isMobileMenuOpen,
  setIsMobileMenuOpen,
  activeGame,
  setActiveGame,
  dbGames
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const isLojista = user?.role_id === 6 || user?.role_id === 1;

  const [topSearch, setTopSearch] = React.useState('');
  const [isProfileMenuOpen, setIsProfileMenuOpen] = React.useState(false);
  const profileMenuRef = React.useRef<HTMLDivElement>(null);
  const [avatarError, setAvatarError] = React.useState(false);

  // Close profile menu on navigation
  React.useEffect(() => {
    setIsProfileMenuOpen(false);
  }, [location.pathname]);

  // Close profile menu when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(event.target as Node)) {
        setIsProfileMenuOpen(false);
      }
    };
    if (isProfileMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isProfileMenuOpen]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (!topSearch.trim() || activeGame === 'All') return;
    
    // Redirect to search page with query
    navigate(`/busca?q=${encodeURIComponent(topSearch.trim())}`);
  };

  const avatarSrc = (isLojista && user?.store_logo ? user.store_logo : user?.avatar) || user?.photoURL || `https://i.pravatar.cc/150?u=${user?.id || 'user'}`;
  const userDisplayName = user?.username || user?.fullname || 'Usuário';
  const userRole = user?.role_name || (user?.role_id === 6 ? 'Lojista' : 'Membro');


  return (
    <div className="flex h-screen overflow-hidden bg-slate-950 text-slate-200">
      {/* Mobile Sidebar Overlay */}
      {isMobileMenuOpen && (
        <div 
          className="fixed inset-0 bg-black/80 z-[60] md:hidden"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside 
        id="app-sidebar"
        className={`
          fixed md:relative inset-y-0 left-0 z-[70]
          ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}
          w-72 md:w-64 bg-slate-900 border-r border-slate-800 flex flex-col transition-transform duration-200 ease-in-out
        `}
      >
        {isLojista ? (
          <LojistaNavigation 
            collapsed={false} 
            onMobileClose={() => setIsMobileMenuOpen(false)} 
          />
        ) : (
          <Sidebar 
            collapsed={false} 
            onMobileClose={() => setIsMobileMenuOpen(false)} 
            activeGame={activeGame}
            setActiveGame={setActiveGame}
            dbGames={dbGames}
          />
        )}
      </aside>

      {/* Main Content */}
      <main className="flex-1 flex flex-col overflow-hidden relative">
        <header className="h-16 bg-slate-900 border-b border-slate-800 flex items-center justify-between px-4 md:px-8 z-40">
          <div className="flex items-center space-x-4">
            <button 
              onClick={() => setIsMobileMenuOpen(true)}
              className="md:hidden w-10 h-10 bg-slate-800/80 border border-slate-700/60 rounded-xl flex items-center justify-center text-slate-300"
            >
              <i className="fas fa-bars"></i>
            </button>
          </div>

          <div className="flex items-center space-x-3 md:space-x-6">
            {user && (
              <>
                <Link to="/notificacoes" className="relative p-2 rounded-xl hover:bg-slate-800 transition-colors group" title="Notificações">
                  <i className="fas fa-bell text-slate-400 group-hover:text-purple-400"></i>
                  {unreadNotifs > 0 && (
                    <span className="absolute top-0 right-0 w-4 h-4 bg-pink-600 text-white text-[9px] flex items-center justify-center rounded-full border border-slate-900 font-bold">
                      {unreadNotifs > 9 ? '9+' : unreadNotifs}
                    </span>
                  )}
                </Link>

                <Link to="/carrinho" className="relative p-2 rounded-xl hover:bg-slate-800 transition-colors group" title="Carrinho">
                  <i className="fas fa-shopping-cart text-slate-400 group-hover:text-purple-400"></i>
                  {cartCount > 0 && (
                    <span className="absolute top-0 right-0 w-4 h-4 bg-pink-600 text-white text-[9px] flex items-center justify-center rounded-full border border-slate-900 font-bold">
                      {cartCount}
                    </span>
                  )}
                </Link>
              </>
            )}
            
            {user ? (
              <div ref={profileMenuRef} className="relative">
                {/* Profile Trigger Button */}
                <button
                  type="button"
                  onClick={() => setIsProfileMenuOpen((prev) => !prev)}
                  className={`flex items-center space-x-2 sm:space-x-3 p-1 sm:pl-1.5 sm:pr-3.5 py-1 rounded-full border transition-colors cursor-pointer select-none ${
                    isProfileMenuOpen
                      ? 'bg-slate-800 border-purple-500/60'
                      : 'bg-slate-800/80 hover:bg-slate-800 border-white/10 hover:border-purple-500/40'
                  }`}
                  aria-expanded={isProfileMenuOpen}
                  aria-label="Menu de perfil do usuário"
                >
                  <div className="relative shrink-0">
                    {!avatarError ? (
                      <img 
                        src={avatarSrc} 
                        onError={() => setAvatarError(true)}
                        className="w-8 h-8 rounded-full ring-2 ring-purple-500/50 object-cover bg-slate-800" 
                        alt={userDisplayName} 
                      />
                    ) : (
                      <div className="w-8 h-8 rounded-full ring-2 ring-purple-500/50 bg-purple-950/80 border border-purple-500/30 flex items-center justify-center text-purple-300 font-bold text-xs">
                        {userDisplayName.charAt(0).toUpperCase()}
                      </div>
                    )}
                  </div>

                  <div className="hidden sm:flex flex-col text-left">
                    <span className="text-xs font-black text-white leading-tight max-w-[110px] truncate">
                      {userDisplayName}
                    </span>
                    <span className="text-[9px] text-purple-400 font-black uppercase tracking-wider leading-none">
                      {userRole}
                    </span>
                  </div>

                  <i className={`fas fa-chevron-down text-[9px] text-slate-400 hidden sm:block ${
                    isProfileMenuOpen ? 'rotate-180 text-purple-400' : ''
                  }`}></i>
                </button>

                {/* Dropdown Menu */}
                {isProfileMenuOpen && (
                  <div className="absolute top-full right-0 mt-2 w-60 sm:w-56 bg-slate-900 border border-purple-500/30 rounded-2xl shadow-2xl z-50 p-2 space-y-1">
                    {/* Mobile User Header */}
                    <div className="sm:hidden px-3 py-2.5 bg-slate-800/50 rounded-xl border border-white/5 mb-1 flex items-center space-x-3">
                      <div className="w-8 h-8 rounded-full ring-2 ring-purple-500/40 bg-slate-800 flex items-center justify-center text-purple-300 font-bold text-xs shrink-0 overflow-hidden">
                        {!avatarError ? (
                          <img src={avatarSrc} alt="" className="w-full h-full object-cover" />
                        ) : (
                          userDisplayName.charAt(0).toUpperCase()
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-black text-white truncate leading-tight">
                          {userDisplayName}
                        </p>
                        <p className="text-[10px] text-purple-400 font-black uppercase tracking-wider truncate mt-0.5">
                          {userRole}
                        </p>
                      </div>
                    </div>

                    <Link 
                      to={isLojista ? `/loja/${user?.username}` : "/perfil"} 
                      onClick={() => setIsProfileMenuOpen(false)}
                      className="flex items-center space-x-2.5 w-full text-left px-3.5 py-2 text-xs font-bold text-slate-200 hover:text-white hover:bg-slate-800 rounded-xl transition-colors"
                    >
                      <i className={`${isLojista ? 'fas fa-store text-amber-400' : 'fas fa-user-circle text-purple-400'} text-sm w-4 text-center`}></i>
                      <span>{isLojista ? 'Ver Minha Loja' : 'Meu Perfil'}</span>
                    </Link>

                    {isLojista && (
                      <Link 
                        to="/minha-loja" 
                        onClick={() => setIsProfileMenuOpen(false)}
                        className="flex items-center space-x-2.5 w-full text-left px-3.5 py-2 text-xs font-bold text-slate-200 hover:text-white hover:bg-slate-800 rounded-xl transition-colors"
                      >
                        <i className="fas fa-gear text-purple-400 text-sm w-4 text-center"></i>
                        <span>Ajustes da Loja</span>
                      </Link>
                    )}

                    {!isLojista && (
                      <Link 
                        to="/decks" 
                        onClick={() => setIsProfileMenuOpen(false)}
                        className="flex items-center space-x-2.5 w-full text-left px-3.5 py-2 text-xs font-bold text-slate-200 hover:text-white hover:bg-slate-800 rounded-xl transition-colors"
                      >
                        <i className="fas fa-layer-group text-blue-400 text-sm w-4 text-center"></i>
                        <span>Meus Decks</span>
                      </Link>
                    )}

                    {!isLojista && (
                      <Link 
                        to="/configuracoes" 
                        onClick={() => setIsProfileMenuOpen(false)}
                        className="flex items-center space-x-2.5 w-full text-left px-3.5 py-2 text-xs font-bold text-slate-200 hover:text-white hover:bg-slate-800 rounded-xl transition-colors"
                      >
                        <i className="fas fa-sliders text-purple-400 text-sm w-4 text-center"></i>
                        <span>Configurações</span>
                      </Link>
                    )}

                    <Link 
                      to={isLojista ? "/termos-do-lojista" : "/termos"} 
                      onClick={() => setIsProfileMenuOpen(false)}
                      className="flex items-center space-x-2.5 w-full text-left px-3.5 py-2 text-xs font-bold text-slate-200 hover:text-white hover:bg-slate-800 rounded-xl transition-colors"
                    >
                      <i className="fas fa-file-lines text-purple-400 text-sm w-4 text-center"></i>
                      <span>{isLojista ? 'Termos do Lojista' : 'Termos e Condições'}</span>
                    </Link>

                    <div className="h-px bg-white/5 my-1" />

                    <button 
                      type="button"
                      onClick={() => {
                        setIsProfileMenuOpen(false);
                        logout();
                      }} 
                      className="flex items-center space-x-2.5 w-full text-left px-3.5 py-2 text-xs font-bold text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-xl transition-colors cursor-pointer"
                    >
                      <i className="fas fa-sign-out-alt text-sm w-4 text-center"></i>
                      <span>Sair da Conta</span>
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <button 
                onClick={() => navigate('/login')}
                className="bg-purple-600 hover:bg-purple-500 text-white text-xs font-bold px-4 py-2 rounded-xl transition-colors shadow-md shadow-purple-600/20"
              >
                Entrar
              </button>
            )}
          </div>
        </header>

        <div className="flex-1 overflow-y-auto bg-slate-950 p-4 md:p-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
};
